import { FirebaseError } from "firebase/app";
import {
  multiFactor,
  TotpMultiFactorGenerator,
  getMultiFactorResolver,
  type User,
  type MultiFactorError,
  type MultiFactorResolver,
  type MultiFactorInfo,
  type TotpSecret,
} from "firebase/auth";
import QRCode from "qrcode";
import { getFirebaseAuth } from "./firebase";

const ISSUER = "Asistent Auto";
const DISPLAY_NAME = "Authenticator app";

/**
 * Begins TOTP enrolment: opens a multi-factor session, generates a shared
 * secret, and renders its `otpauth://` URL to a QR-code data URL for display.
 * The returned `secret` must be passed back to `finishTotpEnrollment`.
 * May reject with `auth/requires-recent-login` — callers reauthenticate first.
 */
export async function startTotpEnrollment(
  user: User
): Promise<{ secret: TotpSecret; qrDataUrl: string }> {
  const session = await multiFactor(user).getSession();
  const secret = await TotpMultiFactorGenerator.generateSecret(session);
  const otpauthUrl = secret.generateQrCodeUrl(user.email ?? ISSUER, ISSUER);
  const qrDataUrl = await QRCode.toDataURL(otpauthUrl);
  return { secret, qrDataUrl };
}

/** Completes enrolment by verifying the user-typed code against the secret. */
export async function finishTotpEnrollment(
  user: User,
  secret: TotpSecret,
  code: string
): Promise<void> {
  const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code);
  await multiFactor(user).enroll(assertion, DISPLAY_NAME);
}

/** The user's enrolled TOTP factors (empty when none). */
export function listTotpFactors(user: User): MultiFactorInfo[] {
  return multiFactor(user).enrolledFactors.filter(
    (f) => f.factorId === TotpMultiFactorGenerator.FACTOR_ID
  );
}

/** Removes an enrolled factor. May reject with `auth/requires-recent-login`. */
export async function unenrollTotpFactor(user: User, factor: MultiFactorInfo): Promise<void> {
  await multiFactor(user).unenroll(factor);
}

/** True when a sign-in error means a second factor is required. */
export function isMfaRequiredError(err: unknown): boolean {
  return err instanceof FirebaseError && err.code === "auth/multi-factor-auth-required";
}

/**
 * Extracts the TOTP challenge from a `multi-factor-auth-required` error.
 * Throws if the error carries no enrolled TOTP factor (we only support TOTP).
 */
export function getTotpChallenge(err: unknown): {
  resolver: MultiFactorResolver;
  hintUid: string;
} {
  const resolver = getMultiFactorResolver(getFirebaseAuth(), err as MultiFactorError);
  const hint = resolver.hints.find(
    (h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID
  );
  if (!hint) throw new Error("no-totp-factor");
  return { resolver, hintUid: hint.uid };
}

/** Completes a sign-in challenge with the user-typed 6-digit code. */
export async function completeTotpChallenge(
  resolver: MultiFactorResolver,
  hintUid: string,
  code: string
): Promise<void> {
  const assertion = TotpMultiFactorGenerator.assertionForSignIn(hintUid, code);
  await resolver.resolveSignIn(assertion);
}
